
import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme, useColor, useBrand } from '../../../context/ColorContext';

const ProductStack = React.memo(({ openLightbox, activeIndex, setActiveIndex }) => {
    const navigate = useNavigate();
    const [dragOffset, setDragOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const startX = useRef(0);
    const { theme } = useTheme();
    const { brandData } = useColor();
    const { activeBrand } = useBrand();
    // Use featured products for homepage; falls back to all products if none featured
    const PRODUCTS = brandData.FEATURED_PRODUCTS || brandData.PRODUCTS;


    const nextProd = () => {
        setActiveIndex((prev) => (prev + 1) % PRODUCTS.length);
        setDragOffset(0);
    };

    const prevProd = () => {
        setActiveIndex((prev) => (prev - 1 + PRODUCTS.length) % PRODUCTS.length);
        setDragOffset(0);
    };

    const handleStart = (e) => {
        setIsDragging(true);
        startX.current = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
    };

    const handleMove = (e) => {
        if (!isDragging) return;
        const currentX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
        const diff = currentX - startX.current;
        setDragOffset(diff);
    };

    const handleEnd = () => {
        if (!isDragging) return;
        setIsDragging(false);
        if (dragOffset > 100) prevProd();
        else if (dragOffset < -100) nextProd();
        setDragOffset(0);
    };

    return (
        <div
            className="relative w-[210px] md:w-[320px] h-[280px] md:h-[420px] mx-auto select-none touch-none"
            onMouseDown={handleStart}
            onMouseMove={handleMove}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
            onTouchStart={handleStart}
            onTouchMove={handleMove}
            onTouchEnd={handleEnd}
        >
            {PRODUCTS.map((prod, index) => {
                let offset = index - activeIndex;
                if (offset < -1) offset += PRODUCTS.length;
                if (offset > 1) offset -= PRODUCTS.length;

                const isActive = offset === 0;

                let translateY = offset * 25;
                let scale = 1 - Math.abs(offset) * 0.1;
                let zIndex = 30 - Math.abs(offset);
                let opacity = 1 - Math.abs(offset) * 0.5;
                let rotate = offset * 5;

                // Apply drag transform to active card
                let transform = `translateY(${translateY}px) scale(${scale}) rotate(${rotate}deg)`;
                if (isActive) {
                    transform = `translateX(${dragOffset}px) translateY(${dragOffset * 0.1}px) rotate(${dragOffset * 0.05}deg) scale(1.02)`;
                }
                return (
                    <div
                        key={prod.id}
                        className={`absolute inset-0 ${theme === 'dark' ? 'bg-black/70 border-white/20' : 'bg-white/80 border-black/10'} backdrop-blur-xl border rounded-[2.5rem] p-4 shadow-2xl flex flex-col items-center justify-center text-center cursor-grab active:cursor-grabbing overflow-hidden`}
                        style={{
                            zIndex,
                            transform: isActive
                                ? `translate3d(${dragOffset}px, ${dragOffset * 0.1}px, 0) rotate(${dragOffset * 0.05}deg) scale(1.02)`
                                : `translate3d(0, ${translateY}px, 0) scale(${scale}) rotate(${rotate}deg)`,
                            opacity: opacity > 0 ? opacity : 0,
                            pointerEvents: isActive ? 'auto' : 'none',
                            transition: isDragging ? 'opacity 0.3s ease, z-index 0.3s ease' : 'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.5s ease, z-index 0.5s ease',
                            willChange: 'transform, opacity',
                            backfaceVisibility: 'hidden',
                            WebkitBackfaceVisibility: 'hidden'
                        }}
                    >
                        {/* Scaled Wrapper: Reduced for Mobile (20% specifically) */}
                        <div className="w-full h-full flex flex-col items-center justify-center scale-[0.65] md:scale-[0.82] origin-center transform">
                            {/* Image Container */}
                            <div className="w-full h-44 relative mb-4 flex items-center justify-center">
                                <img
                                    src={prod.image}
                                    alt={prod.name}
                                    className={`w-full h-full object-contain transition-transform duration-700 ${theme === 'dark' ? 'drop-shadow-[0_20px_40px_rgba(0,0,0,0.4)]' : ''}`}
                                    draggable="false"
                                />
                            </div>

                            {/* Content */}
                            <div className="flex flex-col justify-center gap-2">
                                <h4 className={`font-black text-lg md:text-xl leading-tight uppercase tracking-tight drop-shadow-lg ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                                    {prod.name}
                                </h4>
                                <p className={`text-xs font-medium line-clamp-2 px-4 ${theme === 'dark' ? 'text-white/60' : 'text-slate-600'}`}>
                                    {prod.desc}
                                </p>

                                <div className="mt-4 flex items-center justify-center gap-3">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); prevProd(); }}
                                        className={`p-2 rounded-full transition-colors ${theme === 'dark' ? 'bg-white/5 hover:bg-white/10 text-white' : 'bg-black/5 hover:bg-black/10 text-slate-900'}`}
                                    >
                                        <ChevronLeft size={20} />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); navigate(`/urunler/${prod.slug || prod.id}`); }}
                                        className="px-8 py-2.5 rounded-full text-white text-[11px] font-bold uppercase tracking-widest hover:scale-105 transition-transform"
                                        style={{ backgroundColor: activeBrand.primaryColor }}
                                    >
                                        İNCELE
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); nextProd(); }}
                                        className={`p-2 rounded-full transition-colors ${theme === 'dark' ? 'bg-white/5 hover:bg-white/10 text-white' : 'bg-black/5 hover:bg-black/10 text-slate-900'}`}
                                    >
                                        <ChevronRight size={20} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Swipe Indicators (Top-right) */}
                        <div className="absolute top-6 right-6 flex gap-1.5 scale-90">
                            {PRODUCTS.map((_, i) => (
                                <div
                                    key={i}
                                    className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${i === activeIndex ? 'w-4' : (theme === 'dark' ? 'bg-white/20' : 'bg-black/10')}`}
                                    style={{ backgroundColor: i === activeIndex ? activeBrand.primaryColor : undefined }}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

ProductStack.displayName = 'ProductStack';

export default ProductStack;
